<template>
  <div class="container mx-auto px-4 py-2">
    <!-- Вкладки -->
    <div class="mb-3 border-b border-gray-200">
      <nav class="flex gap-6">
        <button
          type="button"
          :class="['px-1 py-3 border-b-2 text-sm font-medium',
            activeTab === 'tasks'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700']"
          @click="activeTab = 'tasks'"
        >
          Задания
        </button>
        <button
          type="button"
          :class="['px-1 py-3 border-b-2 text-sm font-medium',
            activeTab === 'reestr'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700']"
          @click="onReestrTab"
        >
          Реестр выпуска
        </button>
        <button
          type="button"
          :class="['px-1 py-3 border-b-2 text-sm font-medium',
            activeTab === 'controlled'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700']"
          @click="activeTab = 'controlled'"
        >
          Контролируемое использование
        </button>
        <button
          type="button"
          :class="['px-1 py-3 border-b-2 text-sm font-medium',
            activeTab === 'deviation'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700']"
          @click="activeTab = 'deviation'"
        >
          Разрешение на отклонение
        </button>
        <button
          type="button"
          :class="['px-1 py-3 border-b-2 text-sm font-medium',
            activeTab === 'risk'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700']"
          @click="activeTab = 'risk'"
        >
          Оценка рисков
        </button>
        <button
          type="button"
          :class="['px-1 py-3 border-b-2 text-sm font-medium',
            activeTab === 'nc'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700']"
          @click="activeTab = 'nc'"
        >
          Расследования несоответствия
        </button>
        <button
          type="button"
          :class="['px-1 py-3 border-b-2 text-sm font-medium',
            activeTab === 'correction'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700']"
          @click="activeTab = 'correction'"
        >
          Коррекция продукта
        </button>
        <button
          type="button"
          :class="['px-1 py-3 border-b-2 text-sm font-medium',
            activeTab === 'nonconformity'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700']"
          @click="activeTab = 'nonconformity'"
        >
          Реестр несоответствий
        </button>
        <button
          type="button"
          :class="['px-1 py-3 border-b-2 text-sm font-medium',
            activeTab === 'settings'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700']"
          @click="activeTab = 'settings'"
        >
          Настройки
        </button>
      </nav>
    </div>

    <!-- Вкладка: задания -->
    <div v-if="activeTab === 'tasks'" class="grid grid-cols-12 gap-4">
      <!-- Левая панель -->
      <aside class="col-span-3 bg-white border border-gray-200 rounded-lg p-3 h-[80vh] flex flex-col">
        <div class="flex-shrink-0">
          <h2 class="text-sm font-semibold text-gray-700 mb-3">Задания на проверку</h2>

          <div class="mb-3 space-y-2">
            <input
              v-model="taskSearchQuery"
              type="text"
              placeholder="Поиск: № заказа, S/N, наименование, SKU"
              class="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
            <label class="flex items-center gap-2 text-xs text-gray-700 cursor-pointer select-none">
              <input type="checkbox" v-model="showFinalizedTasks" />
              Показать проверенные
            </label>
          </div>
        </div>

        <div
          ref="tasksPanel"
          class="flex-1 overflow-y-auto"
          @scroll.passive="onTasksScroll"
        >
          <div v-if="loadingTasks" class="text-sm text-gray-500">Загрузка...</div>
          <div v-else-if="tasks.length === 0" class="text-sm text-gray-500">Нет заданий</div>
          <ul v-else class="space-y-2">
            <li
              v-for="task in tasks"
              :key="task.id"
              :id="'smk-task-' + task.id"
              :class="['p-3 rounded border cursor-pointer text-sm',
                selectedTaskId === task.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:bg-gray-50']"
              @click="selectTask(task)"
            >
              <div class="font-medium text-gray-900 flex items-center justify-between gap-2">
                <span>Заказ № {{ getOrderNumber(task) }}</span>
                <span :class="taskStatusClass(task)" class="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                  {{ taskStatusLabel(task) }}
                </span>
              </div>
              <div class="text-gray-600 mt-0.5">
                {{ getTaskProductName(task) }}
              </div>
              <div class="text-gray-500 text-xs mt-1">
                {{ getTaskSerialNumbers(task) }}
              </div>
              <!-- Задание вернули на этап: оно снова в производстве и выпуску не подлежит -->
              <div
                v-if="task.returned_to_work"
                class="mt-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 break-words"
              >
                {{ returnedToWorkNotice(task) }}
              </div>
            </li>
          </ul>
          <div v-if="loadingMoreTasks" class="text-xs text-gray-500 text-center py-2">Загружаем ещё...</div>
          <button
            v-else-if="tasks.length > 0 && tasksHasMore"
            type="button"
            @click="loadTasks(false)"
            class="w-full text-sm text-blue-600 hover:underline py-2"
          >
            Загрузить ещё
          </button>
          <div v-else-if="tasks.length > 0 && !tasksHasMore" class="text-xs text-gray-400 text-center py-2">Это все задания</div>
        </div>
      </aside>

      <!-- Центральная область -->
      <section class="col-span-9 h-[80vh]">
        <div v-if="!selectedTask" class="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-500 h-full">
          Выберите задание слева
        </div>

        <div v-else class="grid grid-cols-3 gap-4 h-full">
          <!-- Акт о выпуске (электронная форма) -->
          <div class="col-span-2 bg-white border border-gray-200 rounded-lg p-4 overflow-y-auto">
            <h3 class="font-semibold text-gray-800 mb-3">
              Акт о выпуске <span v-if="currentBatchProduct">{{ currentBatchProduct.serial_number || 'Без S/N' }}</span>
            </h3>

            <!-- Акт уже заполняли раньше: без этой отметки сохранённый черновик неотличим
                 от предзаполненной формы, и специалист может подтвердить чужую работу -->
            <div
              v-if="draftInfo && !isFinalized"
              class="mb-3 rounded-lg border-2 border-amber-400 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900"
            >
              Черновик заполнил {{ draftInfo.author }}, {{ draftInfo.date }}
            </div>

            <!-- Возврат на этап: изделие снова идёт по маршруту, строки акта сохранены,
                 но выпустить его нельзя до повторного завершения задания (Э08) -->
            <div
              v-if="isTaskReturnedToWork"
              class="mb-3 rounded-lg border-2 border-amber-400 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-900 break-words"
            >
              {{ returnedToWorkNotice(selectedTask) }}
            </div>

            <div v-if="!currentBatchProduct" class="text-sm text-gray-500">
              В задании нет единиц продукции
            </div>

            <template v-else>
              <table class="w-full text-sm border border-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-2 py-1 text-left w-10 border-b">№</th>
                    <th class="px-2 py-1 text-left border-b">Наименование</th>
                    <th class="px-2 py-1 text-left w-72 border-b">Результат</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in checksList"
                    :key="row.num"
                    class="border-t border-gray-200"
                  >
                    <td class="px-2 py-2 align-top">{{ row.num }}</td>
                    <td class="px-2 py-2 align-top">
                      {{ row.name }}
                      <div v-if="row.has_text" class="mt-2">
                        <textarea
                          v-model="checkInputs[row.num].text"
                          :disabled="isFinalized"
                          rows="1"
                          placeholder="Комментарий"
                          class="w-full border border-gray-300 rounded px-2 py-1 text-xs disabled:bg-gray-100 resize-y"
                        ></textarea>
                      </div>
                    </td>
                    <td class="px-2 py-2 align-top">
                      <div class="flex gap-3">
                        <label class="inline-flex items-center gap-1 cursor-pointer">
                          <input
                            type="radio"
                            :name="'check_' + row.num"
                            value="ok"
                            v-model="checkInputs[row.num].result"
                            :disabled="isFinalized"
                          />
                          <span class="text-green-700">Соответствует</span>
                        </label>
                        <label class="inline-flex items-center gap-1 cursor-pointer">
                          <input
                            type="radio"
                            :name="'check_' + row.num"
                            value="not_ok"
                            v-model="checkInputs[row.num].result"
                            :disabled="isFinalized"
                          />
                          <span class="text-red-600">Не соответствует</span>
                        </label>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div class="mt-3">
                <label class="block text-sm text-gray-700 mb-1">Комментарий (для реестра)</label>
                <textarea
                  v-model="commentInput"
                  :disabled="isFinalized"
                  rows="2"
                  class="w-full border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-100 resize-y"
                ></textarea>
              </div>

              <div class="flex flex-wrap gap-2 justify-end mt-4">
                <button
                  v-if="!isFinalized"
                  v-can-write
                  type="button"
                  @click="saveCheck"
                  :disabled="saving || !isDirty"
                  class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium disabled:opacity-50"
                >
                  {{ saving ? 'Сохранение...' : 'Сохранить' }}
                </button>

                <!-- Возврат на этап без остановки: изделие не выпускают и не бракуют,
                     а отправляют переделать операцию (Э08) -->
                <button
                  v-if="canReturnToStage"
                  v-can-write
                  type="button"
                  @click="openReturnToStageModal"
                  :disabled="working || saving"
                  class="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded font-medium disabled:opacity-50"
                >
                  Вернуть на этап
                </button>

                <button
                  v-if="!isFinalized && hasAnyNotOk"
                  v-can-write
                  type="button"
                  @click="rejectCheck"
                  :disabled="working || saving || isTaskReturnedToWork"
                  :title="isTaskReturnedToWork ? 'Изделие возвращено на этап: несоответствие фиксируется на рабочем столе, где оно сейчас в работе' : null"
                  class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium disabled:opacity-50"
                >
                  Карантин
                </button>

                <button
                  v-if="!isFinalized && allOk"
                  v-can-write
                  type="button"
                  @click="approveCheck"
                  :disabled="working || saving || isTaskReturnedToWork"
                  :title="isTaskReturnedToWork ? 'Изделие возвращено на этап: выпуск возможен после повторного завершения производства' : null"
                  class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium disabled:opacity-50"
                >
                  Подтвердить выпуск
                </button>

                <button
                  v-if="isFinalized && currentSmkCheckId"
                  type="button"
                  @click="printAkt"
                  class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded font-medium"
                >
                  Сформировать акт
                </button>
              </div>

              <!-- Штатный возврат на маршрут: открывается согласованным документом качества.
                   Условия возврата решает бэкенд, здесь только показываем результат -->
              <div v-if="isProductStopped" class="mt-5 border-t border-gray-200 pt-4">
                <h4 class="text-sm font-semibold text-gray-800 mb-2">Возврат на маршрут</h4>

                <div v-if="loadingReturnOptions" class="text-sm text-gray-500">Проверяем условия возврата…</div>

                <template v-else-if="returnOptions">
                  <template v-if="returnOptions.is_available">
                    <p v-if="returnOptions.document" class="text-sm text-gray-600 mb-2">
                      Открыт документом: {{ returnOptions.document.title }}
                      <span v-if="returnOptions.document.product_action_label">
                        · {{ returnOptions.document.product_action_label }}
                      </span>
                    </p>
                    <button
                      v-can-write
                      type="button"
                      @click="openReturnModal"
                      class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
                    >
                      Вернуть на маршрут
                    </button>
                  </template>
                  <!-- Причина недоступности приходит готовым текстом -->
                  <p v-else class="text-sm text-gray-600 whitespace-pre-line break-words">
                    {{ returnOptions.reason || 'Возврат на маршрут сейчас недоступен.' }}
                  </p>
                </template>

                <p v-else class="text-sm text-gray-500">Условия возврата на маршрут не получены.</p>
              </div>
            </template>
          </div>

          <!-- Документы для проверки -->
          <div class="col-span-1 bg-white border border-gray-200 rounded-lg p-4 overflow-y-auto">
            <h3 class="font-semibold text-gray-800 mb-3">Документы для проверки</h3>
            <div v-for="section in documentsList" :key="section.key" class="mb-3 last:mb-0 text-sm">
              <template v-if="section.key === 'production_dossier'">
                <div class="flex items-center gap-2">
                  <span class="text-gray-600">{{ section.label }}:</span>
                  <template v-if="section.items.length">
                    <button
                      v-for="item in section.items"
                      :key="item.key"
                      type="button"
                      @click="openPrintDocument(item)"
                      :disabled="printingKey === item.key"
                      class="text-blue-600 hover:underline disabled:opacity-50"
                    >
                      {{ printingKey === item.key ? 'Открываем...' : item.label }}
                    </button>
                  </template>
                  <span v-else class="text-gray-400">Загрузка...</span>
                </div>
              </template>
              <template v-else>
                <div class="text-gray-600 mb-1">{{ section.label }}:</div>
                <div v-if="section.items.length" class="pl-3 space-y-0.5">
                  <div v-for="item in section.items" :key="item.key">
                    <button
                      type="button"
                      @click="openPrintDocument(item)"
                      :disabled="printingKey === item.key"
                      class="text-blue-600 hover:underline disabled:opacity-50"
                    >
                      {{ printingKey === item.key ? 'Открываем...' : item.label }}
                    </button>
                  </div>
                </div>
                <div v-else class="pl-3 text-gray-400">Загрузка...</div>
              </template>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Модалка подтверждения -->
    <div
      v-if="confirmModal.show"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="cancelConfirm"
    >
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h3 class="text-lg font-semibold mb-3">{{ confirmModal.title }}</h3>
        <p class="text-sm text-gray-700 mb-5">{{ confirmModal.message }}</p>
        <!-- Краткое описание несоответствия — только для действий, где оно требуется -->
        <div v-if="confirmModal.withDescription" class="mb-5">
          <label class="block text-sm font-medium text-gray-700 mb-1">Краткое описание несоответствия</label>
          <textarea
            v-model="confirmModal.description"
            rows="4"
            maxlength="2000"
            placeholder="Что именно не соответствует требованиям"
            class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
          ></textarea>
          <p class="text-xs text-gray-500 mt-1">{{ confirmModal.description.length }} из 2000 символов</p>
          <p v-if="confirmModal.error" class="text-sm text-red-600 mt-1">{{ confirmModal.error }}</p>
        </div>
        <div class="flex justify-end gap-2">
          <button
            type="button"
            class="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
            @click="cancelConfirm"
          >
            Отмена
          </button>
          <button
            type="button"
            :class="[
              'px-4 py-2 rounded text-white font-medium',
              confirmModal.variant === 'danger'
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-green-600 hover:bg-green-700'
            ]"
            @click="acceptConfirm"
          >
            {{ confirmModal.confirmLabel }}
          </button>
        </div>
      </div>
    </div>

    <!-- Вкладка: реестр -->
    <div v-else-if="activeTab === 'reestr'" class="bg-white border border-gray-200 rounded-lg p-4 h-[80vh] flex flex-col">
      <div class="flex-shrink-0 flex flex-wrap gap-3 items-end mb-4">
        <div>
          <label class="block text-xs text-gray-600 mb-1">Дата с</label>
          <input type="date" v-model="reestrFilters.date_from" class="px-4 py-2 text-sm border border-gray-300 rounded-lg h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
        </div>
        <div>
          <label class="block text-xs text-gray-600 mb-1">Дата по</label>
          <input type="date" v-model="reestrFilters.date_to" class="px-4 py-2 text-sm border border-gray-300 rounded-lg h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
        </div>
        <button
          type="button"
          @click="resetReestrFilters"
          class="self-end h-[39px] inline-flex items-center px-4 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium"
        >
          Сбросить
        </button>
        <div class="ml-auto">
          <button
            type="button"
            @click="printReestr"
            class="self-end h-[39px] inline-flex items-center px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium"
          >
            Сформировать реестр
          </button>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto">
        <div v-if="loadingReestr" class="text-sm text-gray-500">Загрузка...</div>
        <div v-else-if="reestrRows.length === 0" class="text-sm text-gray-500">Нет данных</div>
        <table v-else class="w-full text-sm">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="px-2 py-2 text-left border-b w-8">
                <input
                  type="checkbox"
                  :checked="allReestrSelected"
                  :indeterminate.prop="someReestrSelected"
                  @change="toggleAllReestr($event)"
                />
              </th>
              <th class="px-2 py-2 text-left border-b">Заказ №</th>
              <th class="px-2 py-2 text-left border-b">Продукт</th>
              <th class="px-2 py-2 text-left border-b">S/N</th>
              <th class="px-2 py-2 text-left border-b">Вариант исполнения</th>
              <th class="px-2 py-2 text-left border-b">Дата передачи</th>
              <th class="px-2 py-2 text-left border-b">Дата выпуска</th>
              <th class="px-2 py-2 text-left border-b">Комментарий</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in reestrRows" :key="row.id" class="border-t border-gray-200">
              <td class="px-2 py-1.5">
                <input
                  type="checkbox"
                  :checked="selectedReestrIds.includes(row.id)"
                  @change="toggleReestrRow(row.id, $event)"
                />
              </td>
              <td class="px-2 py-1.5">{{ row.order_number }}</td>
              <td class="px-2 py-1.5">{{ row.product_name }}</td>
              <td class="px-2 py-1.5">{{ row.serial_number }}</td>
              <td class="px-2 py-1.5">{{ row.variant }}</td>
              <td class="px-2 py-1.5">{{ row.check_date }}</td>
              <td class="px-2 py-1.5">{{ row.release_date }}</td>
              <td class="px-2 py-1.5">{{ row.comment }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex-shrink-0 mt-3 flex items-center justify-between">
        <div class="text-sm text-gray-600">
          <template v-if="reestrPagination.total > 0">
            Показано {{ (reestrPagination.currentPage - 1) * reestrPagination.perPage + 1 }} -
            {{ Math.min(reestrPagination.currentPage * reestrPagination.perPage, reestrPagination.total) }}
            из {{ reestrPagination.total }}
          </template>
          <template v-else>Показано 0 из 0</template>
        </div>
        <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
          <button
            @click="changeReestrPage(reestrPagination.currentPage - 1)"
            :disabled="reestrPagination.currentPage <= 1"
            class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Назад
          </button>
          <template v-for="page in Math.max(1, reestrPagination.lastPage)" :key="page">
            <button
              v-if="page === reestrPagination.currentPage ||
                    page === 1 ||
                    page === reestrPagination.lastPage ||
                    (page >= reestrPagination.currentPage - 1 && page <= reestrPagination.currentPage + 1)"
              @click="changeReestrPage(page)"
              class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium"
              :class="page === reestrPagination.currentPage ? 'bg-blue-50 text-blue-600 z-10' : 'text-gray-500 hover:bg-gray-50'"
            >
              {{ page }}
            </button>
            <span
              v-else-if="page === reestrPagination.currentPage - 2 || page === reestrPagination.currentPage + 2"
              class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
            >
              ...
            </span>
          </template>
          <button
            @click="changeReestrPage(reestrPagination.currentPage + 1)"
            :disabled="reestrPagination.currentPage >= reestrPagination.lastPage"
            class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Вперед
          </button>
        </nav>
      </div>
    </div>

    <!-- Вкладка: Контролируемое использование -->
    <div v-else-if="activeTab === 'controlled'" class="h-[80vh] overflow-hidden">
      <ControlledUseList :module-id="id" />
    </div>

    <!-- Вкладка: Разрешение на отклонение -->
    <div v-else-if="activeTab === 'deviation'" class="h-[80vh] overflow-hidden">
      <DeviationPermitList :module-id="id" />
    </div>

    <!-- Вкладка: Оценка рисков -->
    <div v-else-if="activeTab === 'risk'" class="h-[80vh] overflow-hidden">
      <RiskAssessmentList :module-id="id" />
    </div>

    <!-- Вкладка: Расследования несоответствия -->
    <div v-else-if="activeTab === 'nc'" class="h-[80vh] overflow-hidden">
      <NcInvestigationList :module-id="id" />
    </div>

    <!-- Вкладка: Коррекция продукта -->
    <div v-else-if="activeTab === 'correction'" class="h-[80vh] overflow-hidden">
      <ProductCorrectionList :module-id="id" />
    </div>

    <!-- Вкладка: Реестр несоответствий (ТЗ-7.8).
         Стоит последней ветвью общей цепочки условий вкладок — цепочка начинается выше
         и разрывать её вставкой в середину нельзя (docs/07_todo/AND-211_хрупкая_цепочка_условий_на_столе_смк.md) -->
    <div v-else-if="activeTab === 'nonconformity'" class="h-[80vh] overflow-hidden">
      <NonconformityCaseList :module-id="id" />
    </div>

    <!-- Вкладка: настройки стола. Новые ветки добавляются в КОНЕЦ общей цепочки условий,
         разрывать её вставкой в середину нельзя (см. комментарий выше) -->
    <div v-else-if="activeTab === 'settings'" class="h-[80vh] overflow-hidden">
      <NcResponsibleSettings :module-id="id" />
    </div>

    <!-- Возврат изделия на маршрут по согласованному документу качества -->
    <RouteReturnModal
      :show="returnModalShow"
      :batch-product-id="currentBatchProduct?.id || null"
      :nonconformity-case-id="returnOptions?.nonconformity_case_id || null"
      :initial-options="returnOptions"
      @close="closeReturnModal"
      @returned="onReturnedToRoute"
    />

    <!-- Возврат изделия на этап без остановки: решение специалиста качества при выпуске -->
    <ReturnToStageModal
      :show="returnToStageShow"
      :production-task-id="selectedTaskId"
      :batch-product-id="currentBatchProduct?.id || null"
      :serial-number="currentBatchProduct?.serial_number || ''"
      @close="closeReturnToStageModal"
      @returned="onReturnedToStage"
      @failed="notifyReturnToStageError"
    />
  </div>
</template>

<script>
import axios from 'axios';
import ControlledUseList from '@/pages/smkDesk/ControlledUseList.vue';
import DeviationPermitList from '@/pages/smkDesk/DeviationPermitList.vue';
import RiskAssessmentList from '@/pages/smkDesk/RiskAssessmentList.vue';
import NcInvestigationList from '@/pages/smkDesk/NcInvestigationList.vue';
import ProductCorrectionList from '@/pages/smkDesk/ProductCorrectionList.vue';
import NonconformityCaseList from '@/pages/smkDesk/NonconformityCaseList.vue';
import RouteReturnModal from '@/components/smkDesk/RouteReturnModal.vue';
import ReturnToStageModal from '@/components/smkDesk/ReturnToStageModal.vue';
import NcResponsibleSettings from '@/components/smkDesk/NcResponsibleSettings.vue';
import { useNotificationAutoRead } from '@/composables/useNotificationAutoRead';

const DEFAULT_CHECKS = [
  { num: 1, name: 'Накладная на перемещение компонентов', has_text: false },
  { num: 2, name: 'Список скомплектованных компонентов', has_text: false },
  { num: 3, name: 'Производственное досье со столов сборщика', has_text: false },
  { num: 4, name: 'Производственное досье со столов ОТК (протоколы)', has_text: false },
  { num: 5, name: 'Этикетки основного модуля и принадлежностей', has_text: true },
  { num: 6, name: 'Транспортная этикетка', has_text: true },
  { num: 7, name: 'Упаковочный лист', has_text: false },
  { num: 8, name: 'Соответствие результатов ОТК', has_text: false },
  { num: 9, name: 'Дополнительные отметки', has_text: true },
];

export default {
  components: { ControlledUseList, DeviationPermitList, RiskAssessmentList, NcInvestigationList, ProductCorrectionList, NonconformityCaseList, RouteReturnModal, ReturnToStageModal, NcResponsibleSettings },
  props: {
    id: { type: [String, Number], required: true },
  },
  data() {
    return {
      activeTab: 'tasks',
      tasks: [],
      loadingTasks: false,
      loadingMoreTasks: false,
      tasksPage: 1,
      tasksPerPage: 15,
      tasksHasMore: true,
      selectedTaskId: null,
      taskSearchQuery: '',
      showFinalizedTasks: false,
      documents: {},
      checksList: DEFAULT_CHECKS,
      checkInputs: this.makeInitialCheckInputs(),
      commentInput: '',
      currentSmkCheckId: null,
      currentCheckStatus: null,
      // Кто и когда заполнял акт раньше: { author, date } либо null, если черновика нет
      draftInfo: null,
      savedSnapshot: '',
      saving: false,
      working: false,
      reestrRows: [],
      loadingReestr: false,
      reestrFilters: { date_from: '', date_to: '' },
      selectedReestrIds: [],
      reestrPage: 1,
      reestrPerPage: 20,
      reestrPagination: { currentPage: 1, lastPage: 1, total: 0, perPage: 20 },
      confirmModal: {
        show: false,
        title: '',
        message: '',
        confirmLabel: 'Подтвердить',
        variant: 'primary',
        onConfirm: null,
        withDescription: false, // Показывать поле описания несоответствия
        description: '', // Введённое описание несоответствия
        error: '', // Ошибка локальной валидации описания
      },
      printingKey: null,
      returnOptions: null, // Условия возврата на маршрут по выбранному заданию
      loadingReturnOptions: false,
      returnModalShow: false,
      returnToStageShow: false, // Модалка возврата на этап без остановки (Э08)
    };
  },
  computed: {
    selectedTask() {
      return this.tasks.find(t => t.id === this.selectedTaskId) || null;
    },
    currentBatchProduct() {
      const list = this.selectedTask?.batch_products || this.selectedTask?.batchProducts || [];
      return list[0] || null;
    },
    documentsList() {
      const sections = [
        { key: 'production_dossier', label: 'Производственное досье', items: [] },
        { key: 'labels', label: 'Этикетки', items: [] },
        { key: 'packing_list', label: 'Упаковочный лист', items: [] },
      ];

      const dossier = this.documents.production_dossier;
      if (dossier) {
        sections[0].items.push({
          key: 'production_dossier',
          label: 'Открыть',
          descriptor: dossier,
        });
      }

      const labels = this.documents.labels;
      if (Array.isArray(labels)) {
        labels.forEach((d, i) => {
          sections[1].items.push({
            key: `labels-${i}`,
            label: d.label || 'Документ',
            descriptor: d,
          });
        });
      }

      const packing = this.documents.packing_list;
      if (Array.isArray(packing)) {
        packing.forEach((d, i) => {
          sections[2].items.push({
            key: `packing-${i}`,
            label: d.label || 'Документ',
            descriptor: d,
          });
        });
      }

      return sections;
    },
    allOk() {
      return DEFAULT_CHECKS.every(row => this.checkInputs[row.num]?.result === 'ok');
    },
    hasAnyNotOk() {
      return DEFAULT_CHECKS.some(row => this.checkInputs[row.num]?.result === 'not_ok');
    },
    isApproved() {
      return this.currentCheckStatus === 'approved';
    },
    isRejected() {
      return this.currentCheckStatus === 'rejected';
    },
    isFinalized() {
      return this.isApproved || this.isRejected;
    },
    currentFormSnapshot() {
      return JSON.stringify({
        c: this.buildCheckResultsPayload(),
        t: this.commentInput || '',
      });
    },
    isDirty() {
      return this.currentFormSnapshot !== this.savedSnapshot;
    },
    // Изделие остановлено по несоответствию — только тогда есть смысл в возврате на маршрут
    isProductStopped() {
      return this.selectedTask ? this.taskStatus(this.selectedTask) === 'nc_stopped' : false;
    },
    // Задание вернули на этап, и маршрут оно ещё не прошло заново: выпуск невозможен
    isTaskReturnedToWork() {
      return !!this.selectedTask?.returned_to_work;
    },
    /**
     * «Вернуть на этап» — действие по завершённому и не остановленному заданию:
     * остановленное возвращается на маршрут по документу качества, уже возвращённое
     * ждёт повторного прохождения маршрута, по финализированному решение принято.
     */
    canReturnToStage() {
      return !!this.selectedTask
        && !!this.currentBatchProduct
        && !this.isFinalized
        && !this.isProductStopped
        && !this.isTaskReturnedToWork;
    },
    allReestrSelected() {
      return this.reestrRows.length > 0 && this.selectedReestrIds.length === this.reestrRows.length;
    },
    someReestrSelected() {
      return this.selectedReestrIds.length > 0 && this.selectedReestrIds.length < this.reestrRows.length;
    },
  },
  watch: {
    activeTab(val) {
      // Держим активную вкладку в URL, чтобы она восстанавливалась при возврате
      this.$router.replace({ query: { ...this.$route.query, tab: val } }).catch(() => {});
    },
    '$route.query.taskId'(val) {
      // Клик по уведомлению, когда пользователь уже на столе выпуска: меняется только query
      if (val) this.focusTaskFromQuery();
    },
    taskSearchQuery() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.loadTasks(true), 300);
    },
    showFinalizedTasks() {
      this.loadTasks(true);
    },
    'reestrFilters.date_from'() {
      if (this.activeTab !== 'reestr') return;
      this.reestrPage = 1;
      this.loadReestr();
    },
    'reestrFilters.date_to'() {
      if (this.activeTab !== 'reestr') return;
      this.reestrPage = 1;
      this.loadReestr();
    },
  },
  mounted() {
    // Восстановление активной вкладки из URL (например при возврате из формы КИ)
    const tab = this.$route.query.tab;
    if (['tasks', 'reestr', 'controlled', 'deviation', 'risk', 'nc', 'correction', 'nonconformity', 'settings'].includes(tab)) {
      this.activeTab = tab;
    }
    // Переход по уведомлению на строку реестра несоответствий: вкладка открывается
    // и без явного tab= в адресе
    if (this.$route.query.caseId) {
      this.activeTab = 'nonconformity';
    }
    this.loadTasks(true).then(() => {
      // Переход по уведомлению — открываем и подсвечиваем нужное задание
      if (this.$route.query.taskId) this.focusTaskFromQuery();
    });
    if (this.activeTab === 'reestr') {
      this.loadReestr();
    }
  },
  methods: {
    makeInitialCheckInputs() {
      const inputs = {};
      for (const row of DEFAULT_CHECKS) {
        inputs[row.num] = { result: null, text: '' };
      }
      return inputs;
    },
    async loadTasks(reset = false) {
      if (reset) {
        this.tasksPage = 1;
        this.tasksHasMore = true;
        this.tasks = [];
        if (this.$refs.tasksPanel) this.$refs.tasksPanel.scrollTop = 0;
      }
      if (!this.tasksHasMore) return;
      if (reset ? this.loadingTasks : this.loadingMoreTasks) return;

      if (reset) this.loadingTasks = true;
      else this.loadingMoreTasks = true;

      try {
        const params = {
          page: this.tasksPage,
          per_page: this.tasksPerPage,
        };
        const search = (this.taskSearchQuery || '').trim();
        if (search) params.search = search;
        if (this.showFinalizedTasks) params.show_finalized = 1;
        const { data } = await axios.get('/api/smk/tasks', { params });
        if (data?.success) {
          const chunk = data.data || [];
          this.tasks = reset ? chunk : [...this.tasks, ...chunk];
          this.tasksHasMore = !!data.has_more;
          this.tasksPage += 1;
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка загрузки заданий');
      } finally {
        this.loadingTasks = false;
        this.loadingMoreTasks = false;
      }
    },
    onTasksScroll(event) {
      if (!this.tasksHasMore || this.loadingTasks || this.loadingMoreTasks) return;
      const el = event.target;
      // Догружаем, когда до низа осталось меньше 80px
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
        this.loadTasks(false);
      }
    },
    /**
     * Открыть задание из уведомления (?taskId=) на вкладке заданий
     */
    async focusTaskFromQuery() {
      const taskId = Number(this.$route.query.taskId);
      if (!taskId) return;

      this.activeTab = 'tasks';

      const findTask = () => this.tasks.find(t => Number(t.id) === taskId) || null;
      let task = findTask();

      // Список отдаётся с сортировкой по убыванию id, поэтому новое задание всегда на первой
      // странице. Если пользователь уже сидел на столе, его список загружен ДО появления задания —
      // догрузка вниз такое задание не найдёт никогда, нужна именно перезагрузка с первой страницы.
      if (!task) {
        await this.loadTasks(true);
        task = findTask();
      }

      // Задание может лежать глубже первой порции — догружаем список порциями
      let attempts = 0;
      while (!task && this.tasksHasMore && attempts < 10) {
        attempts += 1;
        await this.loadTasks(false);
        task = findTask();
      }

      if (!task) {
        this.notifyError(null, 'Задание из уведомления не найдено в списке к проверке — возможно, его уже проверили. Включите «Показать проверенные».');
        return;
      }

      await this.selectTask(task);
      this.scrollToTask(taskId);

      // Задание показано — убираем taskId из URL, иначе F5 или возврат на стол повторно
      // дёрнут этот метод и покажут сообщение о «ненайденном» уже проверенном задании
      const query = { ...this.$route.query };
      delete query.taskId;
      this.$router.replace({ query }).catch(() => {});
    },
    /**
     * Прокрутить список к заданию и подсветить его
     */
    scrollToTask(taskId) {
      const tid = Number(taskId);
      if (!tid) return;

      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById('smk-task-' + tid);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          el.classList.add('ring-2', 'ring-green-400', 'ring-offset-2');
          setTimeout(() => {
            el.classList.remove('ring-2', 'ring-green-400', 'ring-offset-2');
          }, 2500);
          return;
        }
        if (attempts++ < 20) {
          setTimeout(tryScroll, 100);
        }
      };
      this.$nextTick(() => setTimeout(tryScroll, 100));
    },
    async selectTask(task) {
      this.selectedTaskId = task.id;
      this.resetCheckForm();
      // Гасим уведомления именно по выбранному заданию, остальные остаются непрочитанными
      useNotificationAutoRead().markEntityRead('smk_task', task.id);
      this.applySavedCheckFromTask(task);
      // Условия возврата спрашиваем только по остановленному изделию — одним запросом на задание
      this.loadReturnOptions();

      try {
        const { data } = await axios.get('/api/smk/documents', {
          params: { production_task_id: task.id },
        });
        if (data?.success) {
          this.documents = data.data || {};
        }
      } catch (e) {
        this.documents = {};
      }
    },
    resetCheckForm() {
      this.checkInputs = this.makeInitialCheckInputs();
      this.commentInput = '';
      this.currentSmkCheckId = null;
      this.currentCheckStatus = null;
      this.draftInfo = null;
      this.documents = {};
      this.returnOptions = null;
      this.savedSnapshot = this.currentFormSnapshot;
    },

    /**
     * Условия возврата изделия на маршрут: открыт ли возврат согласованным документом
     * качества и из каких операций выбирать. Спрашиваем только по остановленному изделию.
     */
    async loadReturnOptions() {
      this.returnOptions = null;
      if (!this.isProductStopped) return;

      const batchProduct = this.currentBatchProduct;
      if (!batchProduct?.id) return;

      this.loadingReturnOptions = true;
      const requestedId = batchProduct.id;
      try {
        const { data } = await axios.post('/api/nonconformity/getReturnOptions', {
          batch_product_id: requestedId,
        });
        // Пользователь мог переключить задание, пока шёл запрос
        if (this.currentBatchProduct?.id !== requestedId) return;
        if (data?.success) this.returnOptions = data.data || null;
      } catch (e) {
        this.notifyError(e, 'Не удалось получить условия возврата на маршрут');
      } finally {
        this.loadingReturnOptions = false;
      }
    },

    openReturnModal() {
      this.returnModalShow = true;
    },

    closeReturnModal() {
      this.returnModalShow = false;
    },

    openReturnToStageModal() {
      this.returnToStageShow = true;
    },

    closeReturnToStageModal() {
      this.returnToStageShow = false;
    },

    /**
     * Изделие вернулось на этап: задание снова в производстве, строки акта сохранены.
     * Перечитываем список — задание останется в нём с припиской и без права на выпуск.
     */
    async onReturnedToStage(message) {
      this.returnToStageShow = false;
      this.notifySuccess(message || 'Изделие возвращено на этап');

      const taskId = this.selectedTaskId;
      await this.loadTasks(true);
      const task = this.tasks.find(t => t.id === taskId) || null;
      if (task) {
        await this.selectTask(task);
      } else {
        this.selectedTaskId = null;
        this.resetCheckForm();
      }
    },

    // Отказ возврата на этап: текст уже человеческий, модалка показывает его у себя
    notifyReturnToStageError(message) {
      this.notifyError(null, message);
    },

    /**
     * Изделие вернулось на маршрут: заново читаем список заданий, проверка СМК сброшена
     * в ожидание, изделие снова пойдёт по маршруту с выбранной операции
     */
    async onReturnedToRoute() {
      const taskId = this.selectedTaskId;
      await this.loadTasks(true);
      const task = this.tasks.find(t => t.id === taskId) || null;
      if (task) {
        await this.selectTask(task);
      } else {
        this.selectedTaskId = null;
        this.resetCheckForm();
      }
    },
    applySavedCheckFromTask(task) {
      const bps = task?.batch_products || task?.batchProducts || [];
      const bp = bps[0];
      const smkCheck = bp?.smk_check || bp?.smkCheck || null;
      if (!smkCheck) return;

      const savedInputs = this.makeInitialCheckInputs();
      const checkResults = smkCheck.check_results || smkCheck.checkResults || {};
      for (const row of DEFAULT_CHECKS) {
        const raw = checkResults[row.num] ?? checkResults[String(row.num)] ?? null;
        if (!raw) continue;
        savedInputs[row.num] = {
          result: raw.result ?? null,
          text: row.has_text ? (raw.text || '') : '',
        };
      }

      this.checkInputs = savedInputs;
      this.commentInput = smkCheck.comment || '';
      this.currentSmkCheckId = smkCheck.id || null;
      this.currentCheckStatus = smkCheck.status || null;
      this.draftInfo = this.buildDraftInfo(smkCheck);
      this.savedSnapshot = this.currentFormSnapshot;
    },
    /**
     * Отметка о заполнении акта: кто и когда сохранял черновик.
     * У актов, заполненных до появления этих полей, отметки нет — тогда null.
     */
    buildDraftInfo(smkCheck) {
      const checkedAt = smkCheck?.checked_at || smkCheck?.checkedAt || null;
      if (!checkedAt) return null;

      const user = smkCheck.checked_by_user || smkCheck.checkedByUser || null;

      return {
        author: this.userName(user),
        date: this.formatCheckedAt(checkedAt),
      };
    },
    /**
     * Человекочитаемое имя сотрудника: ФИО, иначе логин, иначе почта.
     */
    userName(user) {
      if (!user) return 'сотрудник не указан';
      const full = [user.last_name, user.first_name, user.patronymic].filter(Boolean).join(' ');
      return full || user.name || user.email || 'сотрудник не указан';
    },
    /**
     * Момент заполнения акта в виде ДД.ММ.ГГГГ ЧЧ:ММ.
     *
     * Строка разбирается как есть, без перевода в часовой пояс браузера: время в базе
     * хранится «настенным», и перевод сдвинул бы его на разницу поясов.
     */
    formatCheckedAt(value) {
      const parts = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
      if (parts) {
        return `${parts[3]}.${parts[2]}.${parts[1]} ${parts[4]}:${parts[5]}`;
      }

      const parsed = new Date(value);
      if (isNaN(parsed.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(parsed.getDate())}.${pad(parsed.getMonth() + 1)}.${parsed.getFullYear()} `
        + `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
    },
    async saveCheck({ silent = false } = {}) {
      const bp = this.currentBatchProduct;
      if (!bp) return false;
      this.saving = true;
      try {
        const snapshotBeforeRequest = this.currentFormSnapshot;
        const payload = {
          batch_product_id: bp.id,
          check_results: this.buildCheckResultsPayload(),
          comment: this.commentInput || null,
        };
        const { data } = await axios.post('/api/smk/saveCheck', payload);
        if (data?.success) {
          this.currentSmkCheckId = data.data.id;
          this.currentCheckStatus = data.data.status;
          // Отметка о заполнении обновляется сразу: теперь черновик мой, и плашка
          // должна называть меня, а не предыдущего специалиста
          this.draftInfo = this.buildDraftInfo(data.data);
          this.savedSnapshot = snapshotBeforeRequest;
          if (!silent) this.notifySuccess('Результаты проверки сохранены');
          return true;
        }
        this.notifyError(null, data?.message || 'Ошибка сохранения');
        return false;
      } catch (e) {
        this.notifyError(e, 'Ошибка сохранения');
        return false;
      } finally {
        this.saving = false;
      }
    },
    async ensureSavedBeforeAction() {
      if (!this.isDirty && this.currentSmkCheckId) return true;
      return this.saveCheck({ silent: true });
    },
    buildCheckResultsPayload() {
      const out = {};
      for (const row of DEFAULT_CHECKS) {
        const val = this.checkInputs[row.num];
        if (!val) continue;
        out[row.num] = {
          result: val.result,
          text: row.has_text ? (val.text || null) : null,
        };
      }
      return out;
    },
    approveCheck() {
      this.openConfirm({
        title: 'Подтверждение выпуска продукта',
        message: 'Подтвердить выпуск продукта? Продукция перейдёт в статус «Прошёл выпуск продукта».',
        confirmLabel: 'Подтвердить выпуск',
        variant: 'primary',
        onConfirm: this.doApprove,
      });
    },
    async doApprove() {
      this.working = true;
      try {
        if (!(await this.ensureSavedBeforeAction())) return;

        const { data } = await axios.post('/api/smk/approve', {
          smk_check_id: this.currentSmkCheckId,
        });
        if (data?.success) {
          this.currentCheckStatus = data.data.status;
          this.applyFinalizedToTask(this.selectedTaskId, data.data, 'smk_approved');
          this.notifySuccess('Проверка СМК подтверждена');
        } else {
          this.notifyError(null, data?.message || 'Ошибка подтверждения');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка подтверждения');
      } finally {
        this.working = false;
      }
    },
    rejectCheck() {
      this.openConfirm({
        title: 'Отправка на карантин',
        message: 'Точно переместить изделие в карантин? Изделие будет остановлено и отправлено на склад карантина.',
        confirmLabel: 'Карантин',
        variant: 'danger',
        withDescription: true,
        onConfirm: this.doReject,
      });
    },
    async doReject(description) {
      this.working = true;
      try {
        if (!(await this.ensureSavedBeforeAction())) return;

        const { data } = await axios.post('/api/smk/reject', {
          smk_check_id: this.currentSmkCheckId,
          description: description || null,
        });
        if (data?.success) {
          this.currentCheckStatus = data.data.status;
          // Изделие останавливается по несоответствию, а не закрывается как брак
          this.applyFinalizedToTask(this.selectedTaskId, data.data, 'nc_stopped');
          // Изделие остановлено — проверяем, открыт ли по нему возврат на маршрут
          this.loadReturnOptions();
          this.notifySuccess(data.message || 'Изделие остановлено и отправлено на склад карантина');
        } else {
          this.notifyError(null, data?.message || 'Ошибка');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка');
      } finally {
        this.working = false;
      }
    },
    applyFinalizedToTask(taskId, updatedCheck, receiptStatus) {
      const task = this.tasks.find(t => t.id === taskId);
      if (!task) return;
      const bps = task.batch_products || task.batchProducts || [];
      const bp = bps[0];
      if (!bp) return;
      bp.smk_check = updatedCheck;
      bp.receipt_status = receiptStatus;
    },
    openConfirm({ title, message, confirmLabel, variant, onConfirm, withDescription }) {
      this.confirmModal = {
        show: true,
        title: title || 'Подтверждение',
        message: message || '',
        confirmLabel: confirmLabel || 'Подтвердить',
        variant: variant || 'primary',
        onConfirm: onConfirm || null,
        withDescription: !!withDescription,
        description: '',
        error: '',
      };
    },
    cancelConfirm() {
      this.confirmModal = { ...this.confirmModal, show: false, onConfirm: null, description: '', error: '' };
    },
    async acceptConfirm() {
      const description = (this.confirmModal.description || '').trim();
      // Те же правила, что в Form Request: описание необязательно, не длиннее 2000 символов
      if (this.confirmModal.withDescription && description.length > 2000) {
        this.confirmModal = { ...this.confirmModal, error: 'Описание несоответствия длиннее 2000 символов.' };
        return;
      }
      const fn = this.confirmModal.onConfirm;
      this.confirmModal = { ...this.confirmModal, show: false, onConfirm: null, description: '', error: '' };
      if (typeof fn === 'function') {
        await fn(description);
      }
    },
    async printAkt() {
      if (!this.currentSmkCheckId) return;
      await this.openPrintUrl('/api/smk/printAkt', { smk_check_id: this.currentSmkCheckId });
    },
    async openPrintDocument(doc) {
      const descriptor = doc?.descriptor;
      if (!descriptor) return;
      if (descriptor.type === 'internal') {
        const moduleId = this.$route.params.id;
        const routeLocation = descriptor.route
          ? { name: descriptor.route, params: { id: moduleId, ...(descriptor.params || {}) } }
          : null;
        if (routeLocation) {
          const href = this.$router.resolve(routeLocation).href;
          window.open(href, '_blank');
        }
        return;
      }
      if (!descriptor.url) return;
      this.printingKey = doc.key;
      try {
        await this.requestPrint(descriptor.method || 'get', descriptor.url, descriptor.params || {});
      } finally {
        this.printingKey = null;
      }
    },
    async openPrintUrl(url, params = {}) {
      await this.requestPrint('get', url, params);
    },
    async requestPrint(method, url, params = {}) {
      try {
        let response;
        if ((method || 'get').toLowerCase() === 'post') {
          response = await axios.post(url, params);
        } else {
          response = await axios.get(url, { params });
        }
        this.launchPrintWindow(response.data);
      } catch (e) {
        this.notifyError(e, 'Не удалось открыть документ');
      }
    },
    launchPrintWindow(payload) {
      // packingPlace-эндпоинты возвращают HTML строкой, /api/documents/render — JSON {html}
      const html = typeof payload === 'string' ? payload : (payload?.html ?? null);
      if (!html) {
        this.notifyError(null, 'Пустой ответ при печати');
        return;
      }
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        this.notifyError(null, 'Разрешите всплывающие окна');
        return;
      }
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => printWindow.close();
        }, 500);
      };
    },
    onReestrTab() {
      this.activeTab = 'reestr';
      this.reestrPage = 1;
      this.loadReestr();
    },
    async loadReestr() {
      this.loadingReestr = true;
      // Сброс выборки при любом запросе данных
      this.selectedReestrIds = [];
      try {
        const { data } = await axios.get('/api/smk/reestr', {
          params: {
            ...this.cleanFilters(this.reestrFilters),
            page: this.reestrPage,
            per_page: this.reestrPerPage,
          },
        });
        if (data?.success) {
          const d = data.data || {};
          this.reestrRows = d.data || [];
          this.reestrPagination = {
            currentPage: d.current_page || 1,
            lastPage: d.last_page || 1,
            total: d.total || 0,
            perPage: d.per_page || this.reestrPerPage,
          };
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка загрузки реестра');
      } finally {
        this.loadingReestr = false;
      }
    },
    resetReestrFilters() {
      this.reestrFilters.date_from = '';
      this.reestrFilters.date_to = '';
      // watch'еры date_from/date_to сами вызовут loadReestr, но если оба уже были пустыми — дёрнем явно
      this.reestrPage = 1;
      this.loadReestr();
    },
    changeReestrPage(page) {
      this.reestrPage = page;
      this.loadReestr();
    },
    cleanFilters(filters) {
      const out = {};
      for (const [k, v] of Object.entries(filters)) {
        if (v !== '' && v != null) out[k] = v;
      }
      return out;
    },
    toggleAllReestr(event) {
      if (event.target.checked) {
        this.selectedReestrIds = this.reestrRows.map(r => r.id);
      } else {
        this.selectedReestrIds = [];
      }
    },
    toggleReestrRow(id, event) {
      if (event.target.checked) {
        if (!this.selectedReestrIds.includes(id)) this.selectedReestrIds.push(id);
      } else {
        this.selectedReestrIds = this.selectedReestrIds.filter(x => x !== id);
      }
    },
    async printReestr() {
      if (this.selectedReestrIds.length === 0) {
        this.notifyError(null, 'Выберите хотя бы одну строку реестра');
        return;
      }
      await this.openPrintUrl('/api/smk/printReestr', { ids: [...this.selectedReestrIds] });
    },
    getTaskProductName(task) {
      const bps = task.batch_products || task.batchProducts || [];
      const bp = bps[0];
      if (!bp?.product) return '—';
      return bp.product.name || '';
    },
    getOrderNumber(task) {
      const order = task.production_order || task.productionOrder;
      if (!order) return '—';
      return order.number || (order.id != null ? String(order.id) : '—');
    },
    getTaskSerialNumbers(task) {
      const bps = task.batch_products || task.batchProducts || [];
      return bps.map(bp => bp.serial_number || 'без S/N').join(', ');
    },
    /**
     * Приписка у задания, возвращённого на этап: почему его вернули и почему нельзя выпустить.
     * Комментарий пишет специалист качества, он же уходит в реестр несоответствий.
     */
    returnedToWorkNotice(task) {
      const comment = (task?.return_comment || '').trim();
      const reason = comment ? `Возвращено в работу: ${comment}.` : 'Возвращено в работу.';
      return `${reason} Выпуск невозможен до завершения производства`;
    },
    taskStatus(task) {
      const bps = task?.batch_products || task?.batchProducts || [];
      const bp = bps[0];
      if (!bp) return 'pending';
      if (bp.receipt_status === 'smk_approved') return 'approved';
      // Остановка по несоответствию важнее отметки проверки СМК — проверяем её первой
      if (bp.receipt_status === 'nc_stopped') return 'nc_stopped';
      const smkCheck = bp.smk_check || bp.smkCheck || null;
      if (smkCheck?.status === 'rejected') return 'quarantine';
      if (bp.receipt_status === 'defect' || bp.receipt_status === 'rework_in_production') return 'quarantine';
      return 'pending';
    },
    taskStatusLabel(task) {
      const status = this.taskStatus(task);
      if (status === 'approved') return 'Выпущено';
      if (status === 'nc_stopped') return 'Остановлено: несоответствие';
      if (status === 'quarantine') return 'Карантин';
      return 'Ожидает';
    },
    taskStatusClass(task) {
      const status = this.taskStatus(task);
      if (status === 'approved') return 'bg-green-100 text-green-700';
      if (status === 'nc_stopped') return 'bg-red-100 text-red-700';
      if (status === 'quarantine') return 'bg-red-100 text-red-700';
      return 'bg-yellow-100 text-yellow-700';
    },
    notifySuccess(message) {
      if (this.$root?.$toast?.success) {
        this.$root.$toast.success(message);
      }
    },
    notifyError(error, fallback) {
      const payload = error?.response?.data;
      // Подробности валидации (422) не проглатываем — показываем пользователю
      const details = payload?.errors && typeof payload.errors === 'object'
        ? Object.values(payload.errors).flat().filter(Boolean)
        : [];
      const msg = details.length
        ? details.join(' ')
        : (payload?.message || fallback || 'Ошибка');
      if (this.$root?.$toast?.error) {
        this.$root.$toast.error(msg);
      } else {
        alert(msg);
      }
    },
  },
};
</script>
